import { Box, Flex, Skeleton, Text, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { CreditAgreement, Insights } from '@finley-technologies/finley-types';

import {
  chartsInitialEndDateJs,
  CohortConfig,
  defaultCohortCadence,
  defaultMaxSeriesAge,
  onPortfolioRepaymentVintageChartFilterUpdate,
  onSelectDateRange,
  PortfolioChartConfig,
  ProfitsVintageInsight,
  ProfitsVintageTableRow,
  vintageInitialStartDate,
  vintageInitialStartDateJs,
} from '../shared/portfolioChartUtils';
import { VintageChartTemplate } from './VintageChartTemplate';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  getDefaultDashboardDisplayEndDate,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  GetRepaymentVintageInsightsQueryGQLType,
  useGetRepaymentVintageInsightsQuery,
} from 'packages/portfolio/requests/requests.gql.generated';

type ChartTypeOptionProps = {
  icon?: string;
  buttonText?: string;
  selectedChartType: ChartTypeOptions;
  chartType: ChartTypeOptions;
  setChartType: React.Dispatch<React.SetStateAction<ChartTypeOptions>>;
};
export const ChartTypeOption = ({
  icon,
  chartType,
  selectedChartType,
  setChartType,
  buttonText,
}: ChartTypeOptionProps) => {
  const isSelected = chartType === selectedChartType;
  return (
    <Box
      borderRadius="8px"
      border="2px"
      borderColor={colors.gray[200]}
      padding="8px 16px"
      ml="8px"
      height="min-content"
      cursor="pointer"
      onClick={() => setChartType(chartType)}
      background={isSelected ? colors.aurora[400] : 'none'}
      color={isSelected ? 'white' : 'black'}
    >
      {icon ? (
        <Icon icon={icon} size="medium" />
      ) : (
        <Text fontWeight="500">{buttonText}</Text>
      )}
    </Box>
  );
};

type ChartTypeSelectorProps = {
  selectedChartType: ChartTypeOptions;
  setChartType: React.Dispatch<React.SetStateAction<ChartTypeOptions>>;
  showPercentageChartOption: boolean;
};
export const ChartTypeSelector = ({
  selectedChartType,
  setChartType,
  showPercentageChartOption,
}: ChartTypeSelectorProps) => {
  return (
    <Flex>
      {showPercentageChartOption && (
        <ChartTypeOption
          selectedChartType={selectedChartType}
          icon="percent"
          setChartType={setChartType}
          chartType="percentageRepaidOfTotalInitialBalance"
        />
      )}
      <ChartTypeOption
        selectedChartType={selectedChartType}
        icon="dollar-sign"
        setChartType={setChartType}
        chartType="totalRepayments"
      />
      <ChartTypeOption
        selectedChartType={selectedChartType}
        setChartType={setChartType}
        chartType="averageRepaymentPerReceivable"
        buttonText="AVG"
      />
    </Flex>
  );
};

type ChartTypeOptions = keyof Pick<
  Insights.RepaymentsGeneratedCohortInsightsData,
  | 'totalRepayments'
  | 'averageRepaymentPerReceivable'
  | 'percentageRepaidOfTotalInitialBalance'
>;

type RepaymentsVintageChartProps = {
  creditFacilityId: string;
};
export const RepaymentsVintageChart = ({
  creditFacilityId,
}: RepaymentsVintageChartProps) => {
  const toast = useToast();

  const [field, setField] = useState<ChartTypeOptions>(
    'percentageRepaidOfTotalInitialBalance',
  );

  const [
    fieldType,
    setFieldType,
  ] = useState<CreditAgreement.DataTapeField.Type>('percentage');

  const [yAxisLabel, setYAxisLabel] = useState<string>('Percentage Repaid');

  useEffect(() => {
    switch (field) {
      case 'totalRepayments':
        setFieldType('currency');
        setYAxisLabel('Total Repayments');
        break;
      case 'averageRepaymentPerReceivable':
        setFieldType('currency');
        setYAxisLabel('Average Repayment Per Receivable');
        break;
      case 'percentageRepaidOfTotalInitialBalance':
        setFieldType('percentage');
        setYAxisLabel('Percentage Repaid');
        break;
      default:
        finleyAssertUnreachable(field);
    }
  }, [field]);

  const [
    selectedDatapoint,
    setSelectedDatapoint,
  ] = useState<ProfitsVintageInsight | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: vintageInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [
    repaymentVintageInputDataForDateRange,
    setRepaymentVintageInputDataForDateRange,
  ] = useState<Insights.CreditFacilityRepaymentVintageFullData[]>([]);

  const [chartDataWithCohortCadence, setChartDataWithCohortCadence] = useState<
    ProfitsVintageInsight[]
  >([]);
  const [tableDataByOriginationDate, setTableDataByOriginationDate] = useState<
    ProfitsVintageTableRow[]
  >([]);

  const [activeFields, setActiveFields] = useState<
    Partial<keyof ProfitsVintageInsight>[]
  >([]);

  const [chartConfig, setChartConfig] = useState<PortfolioChartConfig<string>>(
    {},
  );

  // Cohort-specific states, powers chart filters
  const [
    selectedCohortConfig,
    setSelectedCohortConfig,
  ] = useState<CohortConfig>({
    cohortBaseCadence: defaultCohortCadence,
    cohortSeriesCadence: defaultCohortCadence,
  });
  const [selectedMaxSeriesAge, setSelectedMaxSeriesAge] = useState<number>(
    defaultMaxSeriesAge,
  );

  const [
    showPercentageChartOption,
    setShowPercentageChartOption,
  ] = useState<boolean>(true);

  // we generate new cohort data when filter selections are changed
  // using the base input daily cohort data for the selected dates
  // all chart filters for vintage aside from the date range picker are run on client without API calls
  useEffect(() => {
    onPortfolioRepaymentVintageChartFilterUpdate({
      repaymentVintageDataForDateRange: repaymentVintageInputDataForDateRange,
      selectedCohortConfig,
      selectedMaxSeriesAge,
      field,
      fieldType: fieldType === 'percentage' ? 'percentage' : 'currency',
      setChartConfig,
      setActiveFields,
      setChartDataWithCohortCadence,
      setTableDataByOriginationDate,
      setSelectedDatapoint,
    });
  }, [
    field,
    fieldType,
    repaymentVintageInputDataForDateRange,
    selectedCohortConfig,
    selectedMaxSeriesAge,
  ]);

  const {
    refetch: refetchRepaymentVintageInsights,
    data: repaymentVintageInsightsData,
    loading: isRepaymentVintageInsightsLoading,
  } = useGetRepaymentVintageInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityParams: {
        creditFacilityId,
        repaymentDateStart: vintageInitialStartDate,
        repaymentDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const inputData = data.getRepaymentVintageInsights;

      const receivableOriginationSummaryMap = _.keyBy(
        inputData.receivableOriginationSummaryInfo,
        (summary) => summary.originationDate,
      );

      // checking if optional field totalInitialBalanceOfOriginatedReceivables exists in receivableOriginationSummaryInfo
      // if it does, this means normalizedInitialBalance is configured for CA
      // we need this data to calculate percentage repaid data for vintage graph
      const initialBalanceDataExists = inputData.receivableOriginationSummaryInfo.some(
        (summary) => !!summary.totalInitialBalanceOfOriginatedReceivables,
      );

      // if !initialBalanceDataExists, we'll hide the percentage chart option and set initial chart type to totalRepayments ($)
      setShowPercentageChartOption(initialBalanceDataExists);
      if (!initialBalanceDataExists) {
        setField('totalRepayments');
      }

      const fullRepaymentVintageData: Insights.CreditFacilityRepaymentVintageFullData[] = inputData.repaymentVintageInputData.map(
        (repaymentInfo) => {
          const originationSummary =
            receivableOriginationSummaryMap[repaymentInfo.cohortBaseDate];
          const fullData: Insights.CreditFacilityRepaymentVintageFullData = {
            cohortBaseDate: repaymentInfo.cohortBaseDate,
            cohortSeriesDate: repaymentInfo.cohortSeriesDate,
            cumulativeRepayments: repaymentInfo.cumulativeRepayments,
            totalInitialBalanceOfOriginatedReceivables:
              originationSummary.totalInitialBalanceOfOriginatedReceivables ??
              undefined,
            originatedReceivablesCount:
              originationSummary.originatedReceivablesCount,
          };
          return fullData;
        },
      );

      setRepaymentVintageInputDataForDateRange(fullRepaymentVintageData);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isRepaymentVintageInsightsLoading || !repaymentVintageInsightsData) {
    return <Skeleton height="700px" />;
  }

  return (
    <VintageChartTemplate
      setSelectedMaxSeriesAge={setSelectedMaxSeriesAge}
      tableDataByOriginationDate={tableDataByOriginationDate}
      selectedCohortConfig={selectedCohortConfig}
      setSelectedCohortConfig={setSelectedCohortConfig}
      chartTitle="Repayments By Vintage"
      activeFields={activeFields}
      setActiveFields={setActiveFields}
      selectedDatapoint={selectedDatapoint}
      setSelectedDatapoint={setSelectedDatapoint}
      yAxisLabel={yAxisLabel}
      chartConfig={chartConfig}
      chartDataWithCohortCadence={chartDataWithCohortCadence}
      chartDataDateRange={chartDataDateRange}
      setChartDataDateRange={setChartDataDateRange}
      fieldType={fieldType}
      onSelectDateRangeHandler={(newDateRange: [Date | null, Date | null]) => {
        return onSelectDateRange<GetRepaymentVintageInsightsQueryGQLType>({
          newDateRange,
          refetch: refetchRepaymentVintageInsights,
          creditFacilityId,
          setChartDataDateRange,
          refetchVariables: {
            creditFacilityParams: {
              creditFacilityId,
              repaymentDateStart:
                newDateRange[0] && newDateRange[1]
                  ? commonDate.dateToFullDateString(newDateRange[0])
                  : vintageInitialStartDate,
              repaymentDateEnd:
                newDateRange[0] && newDateRange[1]
                  ? commonDate.dateToFullDateString(newDateRange[1])
                  : getDefaultDashboardDisplayEndDate(),
            },
          },
        });
      }}
      showOriginatedReceivablesCount
      chartTypeSelector={
        <ChartTypeSelector
          setChartType={setField}
          selectedChartType={field}
          showPercentageChartOption={showPercentageChartOption}
        />
      }
    />
  );
};
